import { useState } from "react";
import { Trophy, Users, Calendar, Copy, Pencil, Send, XCircle, AlarmClock, Share2, Check, RefreshCwOff, Trash2 } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { toBcp47, type Locale } from "../../../i18n";
import type { Challenge } from "../types";
import { ChallengeStatusBadge } from "./ChallengeStatusBadge";
import {
  CHALLENGE_TYPE_LABEL_KEYS,
  CHALLENGE_METRIC_LABEL_KEYS,
  CHALLENGE_METRIC_UNIT_KEYS,
} from "../constants";

interface ChallengeCardProps {
  challenge: Challenge;
  onDuplicate: (challenge: Challenge) => void;
  onPublish: (challenge: Challenge) => void;
  onEdit: (challenge: Challenge) => void;
  onCancel: (challenge: Challenge) => void;
  onDelete: (challenge: Challenge) => void;
  onStopRecurrence: (challenge: Challenge) => void;
}

function formatDate(dateStr: string, locale: Locale): string {
  return new Date(dateStr).toLocaleDateString(toBcp47(locale), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getCreatorName(challenge: Challenge): string | null {
  if (!challenge.creator) return null;
  const { firstName, lastName } = challenge.creator;
  return [firstName, lastName].filter(Boolean).join(" ") || null;
}

const DEEP_LINK_BASE = "https://kommiters.com/join";

function ShareButton({
  entityType,
  entityId,
}: {
  entityType: "challenge" | "event";
  entityId: string;
}) {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);
  const url = `${DEEP_LINK_BASE}/${entityType}/${entityId}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? t("adminUI.challenges.shareLinkCopied") : t("adminUI.challenges.shareLinkCopy")}
      className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-success" />
      ) : (
        <Share2 className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

export function ChallengeCard({
  challenge,
  onDuplicate,
  onPublish,
  onEdit,
  onCancel,
  onDelete,
  onStopRecurrence,
}: ChallengeCardProps) {
  const { t, locale } = useLocale();
  const showPublish = challenge.status === "DRAFT";
  const showCancel =
    challenge.status === "UPCOMING" || challenge.status === "ACTIVE";
  const showEdit =
    challenge.status === "DRAFT" || challenge.status === "UPCOMING";
  const showDelete = challenge.status === "DRAFT";
  const showStopRecurrence =
    challenge.recurrence?.enabled &&
    challenge.status !== "COMPLETED" &&
    challenge.status !== "CANCELLED";
  const creatorName = getCreatorName(challenge);

  // Compute time label
  let timeLabel: string | null = null;
  if (challenge.status === "ACTIVE" && challenge.daysRemaining > 0) {
    timeLabel = t("challenges.card.daysLeft", { count: challenge.daysRemaining });
  } else if (challenge.status === "UPCOMING" && !challenge.isStarted) {
    const startMs =
      new Date(challenge.startDate).getTime() - Date.now();
    const daysUntil = Math.ceil(startMs / (1000 * 60 * 60 * 24));
    if (daysUntil > 0) {
      timeLabel = t("challenges.card.startsIn", { count: daysUntil });
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md">
      {/* Header: title + status badge (top-right) */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-foreground">
            {challenge.name}
          </h3>
          {creatorName && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("challenges.card.by", { name: creatorName })}
            </p>
          )}
        </div>
        <ChallengeStatusBadge
          status={challenge.status}
          recurrence={challenge.recurrence}
        />
      </div>

      {/* Description */}
      {challenge.description && (
        <p className="mb-3 line-clamp-2 text-xs text-muted-foreground">
          {challenge.description}
        </p>
      )}

      {/* Type + Metric badges */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {t(CHALLENGE_TYPE_LABEL_KEYS[challenge.type])}
        </span>
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {t(CHALLENGE_METRIC_LABEL_KEYS[challenge.metric])}
        </span>
      </div>

      {/* Stats row */}
      <div className="mb-3 flex items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Users className="h-3.5 w-3.5" />
          {challenge.participantCount}
        </span>
        <span className="inline-flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" />
          {formatDate(challenge.startDate, locale)} – {formatDate(challenge.endDate, locale)}
        </span>
        {challenge.isPaid && (
          <span className="inline-flex items-center gap-1 text-koins">
            <Trophy className="h-3.5 w-3.5" />
            {challenge.entryFee} Koins
          </span>
        )}
        {timeLabel && (
          <span className="inline-flex items-center gap-1">
            <AlarmClock className="h-3.5 w-3.5" />
            {timeLabel}
          </span>
        )}
      </div>

      {/* Target & Rewards summary */}
      {challenge.type === "HIT_THE_TARGET" && challenge.targetValue && (
        <p className="mb-2 text-xs text-muted-foreground">
          {t("challenges.card.target", { value: challenge.targetValue.toLocaleString(), metric: t(CHALLENGE_METRIC_UNIT_KEYS[challenge.metric]) })}
        </p>
      )}
      {challenge.rewards && challenge.rewards.length > 0 && (
        <p className="mb-3 text-xs text-muted-foreground">
          {t("challenges.card.rewardTiers", { count: challenge.rewards.length })}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 border-t border-border pt-3">
        {showPublish && (
          <button
            type="button"
            onClick={() => onPublish(challenge)}
            className="inline-flex h-7 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Send className="h-3 w-3" />
            {t("challenges.card.publish")}
          </button>
        )}
        {showEdit && (
          <button
            type="button"
            onClick={() => onEdit(challenge)}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            <Pencil className="h-3 w-3" />
            {t("common.edit")}
          </button>
        )}
        <button
          type="button"
          onClick={() => onDuplicate(challenge)}
          className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2.5 text-xs font-medium text-foreground hover:bg-muted"
        >
          <Copy className="h-3 w-3" />
          {t("rewards.duplicate")}
        </button>
        {showCancel && (
          <button
            type="button"
            onClick={() => onCancel(challenge)}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-destructive/30 px-2.5 text-xs font-medium text-destructive hover:bg-destructive/10"
          >
            <XCircle className="h-3 w-3" />
            {t("common.cancel")}
          </button>
        )}
        {showDelete && (
          <button
            type="button"
            onClick={() => onDelete(challenge)}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-destructive/30 px-2.5 text-xs font-medium text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3 w-3" />
            {t("common.delete")}
          </button>
        )}
        {showStopRecurrence && (
          <button
            type="button"
            onClick={() => onStopRecurrence(challenge)}
            title={t("challenges.card.stopRecurring")}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-warning/30 px-2.5 text-xs font-medium text-warning hover:bg-warning/10"
          >
            <RefreshCwOff className="h-3 w-3" />
            {t("challenges.card.stopRecurring")}
          </button>
        )}
        <ShareButton entityType="challenge" entityId={challenge._id} />
      </div>
    </div>
  );
}
